/**
 * Typed REST client.
 *
 * Every response is validated at the boundary and normalized into a `Result`,
 * so components never have to reason about a network failure shape. Session
 * tokens are attached automatically, and no credential ever leaves this module.
 */

import type {
  AsrResponseBody,
  Capabilities,
  DetectRequestBody,
  HealthResponse,
  LanguageDescriptor,
  PublicConfig,
  TtsRequestBody,
  TranslateRequestBody,
  TranslateResponseBody,
} from "@shared/types/api";
import type { LanguageCode, LanguageDetection } from "@shared/types/language";
import type { Transcript } from "@shared/types/transcript";

export interface SessionCredentials {
  readonly sessionId: string;
  readonly sessionToken: string;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly retryable: boolean;
  readonly requestId: string | undefined;
  readonly detail: string | undefined;

  constructor(status: number, code: string, message: string, retryable: boolean, requestId?: string, detail?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.retryable = retryable;
    this.requestId = requestId;
    this.detail = detail;
  }

  /** True when the failure is a lost connection rather than a server verdict. */
  get isNetwork(): boolean {
    return this.code === "network" || this.status === 0;
  }
}

type Result<T> = { ok: true; value: T } | { ok: false; error: ApiError };

const ok = <T>(value: T): Result<T> => ({ ok: true, value });
const fail = <T>(error: ApiError): Result<T> => ({ ok: false, error });

export class DreamTongueApi {
  #baseUrl: string;
  #credentials: SessionCredentials | null = null;

  /**
   * @param baseUrl Where the Worker lives. Empty means "same origin as this
   *   page", which is the single-origin case where a Worker serves its own
   *   assets. A split deployment — the SPA on Pages, the Worker for the API —
   *   passes the Worker's origin here, which is what `VITE_API_BASE` is for.
   */
  constructor(baseUrl = "") {
    this.#baseUrl = baseUrl.replace(/\/$/, "");
  }

  /** The origin every request and the WebSocket are resolved against. */
  get baseUrl(): string {
    return this.#baseUrl;
  }

  setCredentials(credentials: SessionCredentials | null): void {
    this.#credentials = credentials;
  }

  get credentials(): SessionCredentials | null {
    return this.#credentials;
  }

  // -- meta -----------------------------------------------------------------

  async health(): Promise<Result<HealthResponse>> {
    return this.#get<HealthResponse>("/api/health");
  }

  async config(): Promise<Result<PublicConfig>> {
    return this.#get<PublicConfig>("/api/config");
  }

  async languages(): Promise<Result<{ languages: LanguageDescriptor[] }>> {
    return this.#get<{ languages: LanguageDescriptor[] }>("/api/languages");
  }

  // -- sessions -------------------------------------------------------------

  async createSession(options: {
    sourceLanguage?: string;
    targetLanguage?: string;
    mode?: "solo" | "two-way";
    persistTranscript?: boolean;
  }): Promise<
    Result<{
      sessionId: string;
      sessionToken: string;
      websocketUrl: string;
      limits: Record<string, number>;
    }>
  > {
    const result = await this.#post<{
      sessionId: string;
      sessionToken: string;
      websocketUrl: string;
      limits: Record<string, number>;
    }>("/api/session", options);

    if (result.ok) this.setCredentials({ sessionId: result.value.sessionId, sessionToken: result.value.sessionToken });
    return result;
  }

  async endSession(): Promise<Result<true>> {
    const result = await this.#request<null>("DELETE", this.#sessionPath("/api/session"), undefined);
    this.setCredentials(null);
    return result.ok ? ok(true) : result;
  }

  async usage(): Promise<Result<Record<string, unknown>>> {
    return this.#get<Record<string, unknown>>(this.#sessionPath("/api/session"));
  }

  /**
   * Append the current session id as a query parameter.
   *
   * The session routes read the id from `?session=` (or an `x-session-id`
   * header). Without it the server rejects the request as malformed — a 400
   * that looks like a server fault but is a client one.
   */
  #sessionPath(path: string): string {
    const id = this.#credentials?.sessionId;
    if (!id) return path;
    return `${path}${path.includes("?") ? "&" : "?"}session=${encodeURIComponent(id)}`;
  }

  async transcript(id?: string): Promise<Result<Transcript>> {
    const target = id ?? this.#credentials?.sessionId;
    if (!target) return fail(new ApiError(0, "bad_request", "No session to fetch a transcript for.", false));
    const result = await this.#get<{ transcript: Transcript }>(`/api/transcript?id=${encodeURIComponent(target)}`);
    return result.ok ? ok(result.value.transcript) : result;
  }

  // -- text -----------------------------------------------------------------

  async translate(body: TranslateRequestBody): Promise<Result<TranslateResponseBody>> {
    return this.#post<TranslateResponseBody>("/api/translate", body);
  }

  async detect(body: DetectRequestBody): Promise<Result<LanguageDetection>> {
    const result = await this.#post<{ detection: LanguageDetection }>("/api/detect", body);
    return result.ok ? ok(result.value.detection) : result;
  }

  // -- audio ----------------------------------------------------------------

  async synthesize(text: string, language: LanguageCode, voice?: string): Promise<Result<ArrayBuffer>> {
    const body: TtsRequestBody = { text, language, ...(voice ? { voice } : {}) };
    return this.#postBinary("/api/tts", body);
  }

  async transcribe(
    audio: ArrayBuffer,
    mimeType: string,
    language: LanguageCode | "auto",
    detectLanguage: boolean,
  ): Promise<Result<AsrResponseBody>> {
    const base64 = arrayBufferToBase64(audio);
    return this.#post<AsrResponseBody>("/api/asr", { audioBase64: base64, mimeType, language, detectLanguage });
  }

  /**
   * Base URL for self-hosted Piper voice files (`/api/voices/*` on the
   * Worker). Empty in a single-origin deployment, where it resolves against
   * the page itself.
   */
  voiceBaseUrl(): string {
    return `${this.#baseUrl}/api/voices`;
  }

  // -- internals ------------------------------------------------------------

  async #get<T>(path: string): Promise<Result<T>> {
    return this.#request<T>("GET", path, undefined);
  }

  async #post<T>(path: string, body: unknown): Promise<Result<T>> {
    return this.#request<T>("POST", path, body);
  }

  async #postBinary(path: string, body: unknown): Promise<Result<ArrayBuffer>> {
    const response = await this.#send(path, { method: "POST", body: JSON.stringify(body), headers: { "content-type": "application/json" } });
    if (response.error) return fail(response.error);
    const buffer = await response.response.arrayBuffer();
    if (buffer.byteLength === 0) return fail(new ApiError(502, "bad_response", "The server returned empty audio.", true));
    return ok(buffer);
  }

  async #request<T>(method: string, path: string, body: unknown): Promise<Result<T>> {
    const sent = await this.#send(path, {
      method,
      ...(body === undefined ? {} : { body: JSON.stringify(body), headers: { "content-type": "application/json" } }),
    });
    if (sent.error) return fail(sent.error);

    if (sent.response.status === 204) return ok(undefined as T);

    try {
      return ok((await sent.response.json()) as T);
    } catch {
      return fail(new ApiError(sent.response.status, "bad_response", "The server returned a malformed response.", true));
    }
  }

  async #send(
    path: string,
    init: RequestInit,
  ): Promise<{ response: Response; error?: undefined } | { response?: undefined; error: ApiError }> {
    const headers = new Headers(init.headers);
    headers.set("accept", "application/json");
    // The token goes on everything once we have one — including `GET` and
    // `DELETE` on `/api/session`, which are scoped to the session itself. It is
    // withheld only when *minting* a session, where there is nothing to prove
    // yet. Excluding the whole path instead silently stripped the credential
    // from both session-scoped verbs and every one of them 403'd.
    const minting = init.method === "POST" && path === "/api/session";
    if (this.#credentials && !minting) {
      headers.set("x-session-token", this.#credentials.sessionToken);
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 30_000);

    try {
      const response = await fetch(`${this.#baseUrl}${path}`, { ...init, headers, signal: controller.signal });
      if (!response.ok) return { error: await readError(response) };
      return { response };
    } catch (error) {
      const aborted = error instanceof Error && error.name === "AbortError";
      return {
        error: new ApiError(
          0,
          "network",
          aborted ? "The request timed out." : "Cannot reach the server. Check your connection.",
          true,
        ),
      };
    } finally {
      clearTimeout(timer);
    }
  }
}

async function readError(response: Response): Promise<ApiError> {
  const requestId = response.headers.get("x-request-id") ?? undefined;
  try {
    const body = (await response.json()) as { error?: { code?: string; message?: string; retryable?: boolean; detail?: string } };
    if (body?.error) {
      return new ApiError(
        response.status,
        body.error.code ?? "internal",
        body.error.message ?? "Something went wrong.",
        body.error.retryable ?? response.status >= 500,
        requestId,
        body.error.detail,
      );
    }
  } catch {
    // Fall through to the generic message.
  }
  return new ApiError(response.status, "internal", `Request failed with status ${response.status}.`, response.status >= 500, requestId);
}

export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/** Conservative default used when `/api/config` has not loaded yet. */
export const FALLBACK_CAPABILITIES: Capabilities = {
  translation: { available: false, provider: "unknown", model: null, context: false },
  speech: { available: false, provider: "none", model: null, detectLanguage: false },
  tts: { available: false, provider: "none", model: null },
  detection: { available: false, provider: "none", model: null },
  languages: [],
};
